import { useEffect, useRef, useState } from 'react';
import {
  MIN_SEARCH_QUERY_LENGTH,
  SEARCH_DEBOUNCE_MS,
  type SearchEntityType,
  type SearchResultGroup,
  type SearchResultsResponse,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';

export type GlobalSearchState = {
  readonly groups: ReadonlyArray<SearchResultGroup>;
  readonly loading: boolean;
  readonly failed: boolean;
  /** The query the current `groups` describe — never the one being typed. */
  readonly settledQuery: string;
};

const IDLE: GlobalSearchState = { groups: [], loading: false, failed: false, settledQuery: '' };

/**
 * Runs the palette's search: waits out the typing, asks once, and keeps only
 * the answer to the newest question.
 *
 * The race guard is a monotonic request id rather than an `AbortController`:
 * aborting stops the network work but not the resolution order of the promises
 * already in flight, so a slow first response could still land after a fast
 * second one and repaint the palette with results for a query the member has
 * already typed past.
 */
export function useGlobalSearch(
  workspaceId: string,
  query: string,
  enabled: boolean,
  types?: ReadonlyArray<SearchEntityType>,
): GlobalSearchState {
  const [state, setState] = useState<GlobalSearchState>(IDLE);
  const latestRequestId = useRef(0);
  const trimmed = query.trim();
  const active = enabled && trimmed.length >= MIN_SEARCH_QUERY_LENGTH;
  // Keyed by value: a caller passing a fresh array literal must not re-run the search.
  const typesKey = types?.join(',');

  useEffect(() => {
    if (!active) {
      // Bump the id so an in-flight response for an older query cannot repaint
      // the palette after the member cleared the field.
      latestRequestId.current += 1;
      setState(IDLE);

      return;
    }

    const requestId = (latestRequestId.current += 1);
    const isStale = () => requestId !== latestRequestId.current;

    setState((previous) => ({ ...previous, loading: true, failed: false }));

    const timer = setTimeout(() => {
      void (async () => {
        try {
          const result = await apiClient.search.query({
            pathParams: { workspaceId },
            query: {
              q: trimmed,
              ...(typesKey === undefined ? {} : { types: typesKey.split(',') as SearchEntityType[] }),
            },
          });

          if (isStale()) return;

          if (!result.ok) {
            setState({ groups: [], loading: false, failed: true, settledQuery: trimmed });

            return;
          }

          const body = result.body as SearchResultsResponse;

          setState({ groups: body.groups, loading: false, failed: false, settledQuery: trimmed });
        } catch {
          if (isStale()) return;

          setState({ groups: [], loading: false, failed: true, settledQuery: trimmed });
        }
      })();
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [active, trimmed, typesKey, workspaceId]);

  return state;
}
